"use client";
import { useEffect, useRef } from "react";

const PDFViewer = ({
    documentId,
    jwt,
    style,
    hideDownloadButton,
    isFullHeight,
}: {
    documentId: string;
    jwt?: string | null | undefined;
    hideDownloadButton?: boolean;
    isFullHeight?: boolean;
    style?: React.CSSProperties;
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const instance = useRef<any>(null);
    const pdfSet = useRef(false);

    // biome-ignore lint: useExhaustiveDependencies
    useEffect(() => {
        const container = containerRef.current;

        if (!documentId || !container || !jwt) return;
        let PSPDFKit: any;

        const loadPSPDFKit = async (retryCount = 0) => {
            try {
                pdfSet.current = true;
                PSPDFKit = await import("pspdfkit");

                instance.current = await PSPDFKit.load({
                    theme: PSPDFKit.Theme.DARK,
                    authPayload: { jwt },
                    container,
                    documentId: documentId,
                    serverUrl: "https://pdf.knowt.com/",
                    baseUrl: `${window.location.protocol}//${window.location.host}/`,
                    styleSheets: [`/pspdfkit-assets/pdfViewer.css`],
                    instant: true,
                    enableHistory: true,
                    toolbar: false,
                    toolbarItems: [
                        ...PSPDFKit.defaultToolbarItems.filter(tool => {
                            if (hideDownloadButton && tool.type === "export-pdf") {
                                return false;
                            }
                            return true;
                        }),
                        { type: "content-editor" },
                    ],
                });

                return instance.current;
            } catch (error) {
                // biome-ignore lint: suspicious/noConsole
                console.log("error", error);
                if (error.name === "ChunkLoadError" && retryCount < 3) {
                    // Wait for a short delay before retrying
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    return loadPSPDFKit(retryCount + 1);
                }
                throw error;
            }
        };

        if (!pdfSet.current)
            loadPSPDFKit()
                // biome-ignore lint: suspicious/noConsole
                .catch(e => console.error(":(((((((( Failed to load PDF Viewer. Error:", e));

        return () => {
            if (instance.current && pdfSet.current) {
                PSPDFKit?.unload(container);
                pdfSet.current = false;
                instance.current = null;
            }
        };
    }, [pdfSet.current]);

    return (
        <div
            className="PSPDFKit-Document"
            style={{
                minWidth: "30rem",
                width: "100%",
                height: isFullHeight ? "100vh" : "calc(100vh - var(--navbar-height) - 13rem)",
                overflow: "hidden",
                position: "relative",
                ...style,
            }}
            data-testid="auto-id-9b7cf7a7-b1d4-4949-82e0-aa63c7878a46">
            <div
                ref={containerRef}
                style={{
                    height: "100%",
                    width: "100%",
                    minWidth: "30rem",
                    borderRadius: "3.2rem",
                    overflow: "hidden",
                }}
                data-testid="auto-id-cab44bf0-4e51-4e4f-ab90-92479c525287"
            />
        </div>
    );
};

export default PDFViewer;
